import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/primitives';
import { api, ApiError, errorMessage, getTenantSlug } from '@/lib/api';
import type { PortalRepairDetail } from '@/lib/types';
import { RepairDetailContent, type Accept } from '@/pages/portal/portal-repairs';
import { GuestCodeGate } from '@/pages/tickets/guest-ticket-page';

/** Reparatur ohne Kundenkonto: Link aus der E-Mail + Einmalcode an die hinterlegte Adresse. */
export function GuestRepairPage() {
  const { token = '' } = useParams<{ token: string }>();
  const base = `/public/repair-access/${encodeURIComponent(token)}`;
  return (
    <GuestCodeGate base={base} token={token} noun="Reparatur" openLabel="Reparatur öffnen">
      {(session, close) => <GuestRepairView base={base} token={token} session={session} onExpired={close} />}
    </GuestCodeGate>
  );
}

/** Datei mit Sitzungskopf laden (Bilder und PDFs lassen sich nicht per einfachem Link mit Kopf abrufen). */
async function fetchBlob(path: string, session: string) {
  const headers: Record<string, string> = { 'x-guest-session': session };
  const slug = getTenantSlug();
  if (slug) headers['x-tenant-slug'] = slug;
  const res = await fetch(`/api${path}`, { headers, credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, 'ERROR', res.status === 401 ? 'Sitzung abgelaufen' : `Fehler ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

function GuestRepairView({ base, token, session, onExpired }: { base: string; token: string; session: string; onExpired: () => void }) {
  const qc = useQueryClient();
  const headers = { 'x-guest-session': session };
  const key = ['guest-repair', token, 'detail'];
  const { data: r, error } = useQuery({ queryKey: key, queryFn: () => api<PortalRepairDetail>(`${base}/repair`, { headers }), retry: false });
  const [photos, setPhotos] = useState<Record<string, string>>({});
  const decide = useMutation({
    mutationFn: ({ approved, note, accept }: { approved: boolean; note: string; accept: Accept }) =>
      api(`${base}/quote/decision`, { method: 'POST', body: { approved, note: note || undefined, ...(approved ? { accept } : {}) }, headers }),
    onSuccess: (_d, v) => {
      toast.success(v.approved ? 'Vielen Dank – wir setzen die Reparatur fort.' : 'Ihre Ablehnung wurde übermittelt.');
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => (e instanceof ApiError && e.status === 401 ? onExpired() : toast.error(errorMessage(e))),
  });

  // Fotos mit Sitzung laden und als lokale Adressen anzeigen
  useEffect(() => {
    if (!r?.photos.length) return;
    let alive = true;
    const urls: string[] = [];
    void Promise.all(
      r.photos.map(async (p) => {
        const url = await fetchBlob(`${base}/photos/${p.id}`, session).catch(() => null);
        if (url) urls.push(url);
        return [p.id, url] as const;
      }),
    ).then((list) => alive && setPhotos(Object.fromEntries(list.filter(([, u]) => u)) as Record<string, string>));
    return () => {
      alive = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [r?.photos, base, session]);

  const expired = error instanceof ApiError && error.status === 401;
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);
  if (expired) return null;
  if (!r) return <Skeleton className="m-8 h-96" />;

  return (
    <div className="min-h-dvh bg-gradient-to-b from-primary/8 via-background to-background px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onExpired}>
            <LogOut /> Schließen
          </Button>
        </div>
        <RepairDetailContent
          r={r}
          decide={(approved, note, accept) => decide.mutate({ approved, note, accept })}
          deciding={decide.isPending ? (decide.variables?.approved ?? null) : null}
          photoSrc={(id) => photos[id]}
          documentLink={(type) => ({
            onClick: () => {
              // Fenster sofort öffnen (Popup-Blocker), Inhalt nachladen
              const win = window.open('', '_blank');
              fetchBlob(`${base}/documents/${type}`, session)
                .then((url) => (win ? (win.location.href = url) : window.open(url, '_blank')))
                .catch((e) => {
                  win?.close();
                  if (e instanceof ApiError && e.status === 401) onExpired();
                  else toast.error(errorMessage(e));
                });
            },
          })}
        />
      </div>
    </div>
  );
}
